// frontend/src/components/athleteEditor/IdentityBar.jsx
import React from 'react';
import { ArrowUpRightIcon, CheckIcon } from './staffProfileIcons';
import { fmtMonthYear } from '../../lib/staffProfileFormat';

const NO_PUBLIC_PROFILE = 'This athlete has no public profile yet.';

const mono = s => String(s || '').toUpperCase();

const ATHLETE_ROLES = ['athlete', 'base_user', 'player_parent'];
const humanizeRole = r => (r ? String(r).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : '');

// The photo the athlete marked primary (primary_photo_index 1-3), else the
// first one they have.
export function primaryPhoto(profile) {
  const p = profile || {};
  const slots = [p.player_photo, p.player_photo_2, p.player_photo_3];
  return slots[(p.primary_photo_index || 1) - 1] || slots.find(Boolean) || null;
}

const initialsOf = (u) => `${(u.first_name || '').trim()[0] || ''}${(u.last_name || '').trim()[0] || ''}`.toUpperCase();

/**
 * Header card for the staff/partner read view (the `.adm` look). Replaces the
 * IdentityBar in READ mode only: Edit mode, and the coach view of this page,
 * still render IdentityBar below. Same action set, same gates: a handler that
 * is null hides its button, exactly as IdentityBar does.
 */
export function ProfileHeaderCard({
  profile, user, perms, counts,
  onSendMessage, onConcierge, onEnroll, onAddToReport, onShare, onEdit, publicProfileUrl, membership,
}) {
  const p = profile || {};
  const u = user || {};
  const c = counts || {};
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || '(unnamed)';
  const photo = primaryPhoto(p);
  const isConcierge = !!(membership && membership.is_concierge);
  const conciergeEnds = membership && fmtMonthYear(membership.concierge_expires_at);
  const roleLabel = ATHLETE_ROLES.includes(u.role) || !u.role ? 'Athlete' : humanizeRole(u.role);
  const line1 = [p.position_primary, p.graduation_year && `Class of ${p.graduation_year}`].filter(Boolean).join(' · ');
  const place = [p.city, p.state || p.high_school_state].filter(Boolean).join(', ');
  const line2 = [p.high_school, place].filter(Boolean).join(' · ');

  return (
    <header className="adm__card adm-phead">
      {photo
        ? <img className="adm-phead__avatar" src={photo} alt={name} />
        : <div className="adm-phead__avatar" aria-hidden="true">{initialsOf(u)}</div>}
      <div className="adm-phead__body">
        <div className="adm-phead__title">
          <h1 className="adm-phead__name">{name}</h1>
          <span className="adm-chip adm-chip--green">{roleLabel}</span>
          {isConcierge && (
            <span className="adm-chip adm-chip--amber">
              Concierge{conciergeEnds ? ` · ends ${conciergeEnds}` : ''}
            </span>
          )}
        </div>
        {line1 && <p className="adm-phead__meta">{line1}</p>}
        {line2 && <p className="adm-phead__meta">{line2}</p>}
        <div className="adm-phead__counts">
          <span className="adm-phead__verified"><CheckIcon size={13} width={2.5} />{c.verified || 0} verified</span>
          <span>{c.unverified || 0} unverified</span>
          <span>{c.empty || 0} empty</span>
        </div>
      </div>
      <div className="adm-phead__actions">
        {onConcierge && <button type="button" className="adm-btn" onClick={onConcierge}>Concierge</button>}
        {onEnroll && <button type="button" className="adm-btn" onClick={onEnroll}>Enroll in Concierge</button>}
        {onAddToReport && <button type="button" className="adm-btn" onClick={onAddToReport}>Add to report</button>}
        {onShare && (
          <button type="button" className="adm-btn" data-testid="athlete_share" onClick={onShare}
            disabled={!publicProfileUrl} title={publicProfileUrl ? undefined : NO_PUBLIC_PROFILE}>
            Share with coaches
          </button>
        )}
        {perms && perms.canEditProfile && onEdit && <button type="button" className="adm-btn" onClick={onEdit}>Edit</button>}
        {publicProfileUrl && (
          <a className="adm-btn" href={publicProfileUrl} target="_blank" rel="noopener noreferrer">
            Public profile <ArrowUpRightIcon />
          </a>
        )}
        <button type="button" className="adm-btn adm-btn--primary" onClick={onSendMessage}>Send message</button>
      </div>
    </header>
  );
}

export default function IdentityBar({
  profile, user, perms, counts, editMode, onToggleEdit,
  savedAgo, saveError,
  onBack, onSendMessage, onConcierge, onEnroll, onAddToReport, onShare, publicProfileUrl, membership,
}) {
  const u = user || {};
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || '(unnamed)';
  const photo = profile.player_photo || profile.player_photo_2 || profile.player_photo_3;
  const isConcierge = !!(membership && membership.is_concierge);
  const conciergeEnds = membership && membership.concierge_expires_at;
  const metaBits = [
    profile.position_primary, profile.graduation_year && `Class of ${profile.graduation_year}`,
    profile.high_school && [profile.high_school, profile.high_school_state].filter(Boolean).join(', '),
    u.uid && `UID ${u.uid}`,
  ].filter(Boolean);

  return (
    <div className={`ae-idbar${editMode ? ' ae-idbar--edit' : ''}`}>
      <button type="button" className="ae-idbar__back" onClick={onBack} aria-label="Back">←</button>
      {photo
        ? <img className="ae-idbar__photo" src={photo} alt={name} />
        : <div className="ae-idbar__photo" aria-hidden="true" />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div className="ae-idbar__name">{name}</div>
          {isConcierge && (
            <span className="ae-idbar__chip-concierge">
              CONCIERGE{conciergeEnds ? ` · ENDS ${conciergeEnds}` : ''}
            </span>
          )}
        </div>
        {editMode
          ? <span className="ae-idbar__chip-amber">✎ Admin edit mode — every change autosaves &amp; is logged</span>
          : <div className="ae-idbar__meta">{metaBits.map(mono).join(' · ')}</div>}
        {!editMode && (
          <div className="ae-idbar__summary">
            <span className="ae-idbar__summary--v">✓ {counts.verified} VERIFIED</span>
            <span className="ae-idbar__summary--rest">{' · '}{counts.unverified} UNVERIFIED · {counts.empty} EMPTY</span>
          </div>
        )}
      </div>
      <div className="ae-idbar__actions">
        {editMode ? (
          <>
            <span className={`ae-idbar__saved${saveError ? ' ae-idbar__saved--err' : ''}`}>
              {saveError ? saveError : (savedAgo && `✓ SAVED ${savedAgo}`)}
            </span>
            <button className="ae-idbar__btn ae-idbar__btn--solid" onClick={onToggleEdit}>DONE EDITING</button>
          </>
        ) : (
          <>
            {onConcierge && <button className="ae-idbar__btn" onClick={onConcierge}>CONCIERGE</button>}
            {onEnroll && <button className="ae-idbar__btn" onClick={onEnroll}>ENROLL IN CONCIERGE</button>}
            {onAddToReport && <button className="ae-idbar__btn" onClick={onAddToReport}>ADD TO REPORT</button>}
            {onShare && (
              <button className="ae-idbar__btn" data-testid="athlete_share" onClick={onShare}
                disabled={!publicProfileUrl} title={publicProfileUrl ? undefined : NO_PUBLIC_PROFILE}>
                SHARE WITH COACHES
              </button>
            )}
            {perms.canEditProfile && <button className="ae-idbar__btn" onClick={onToggleEdit}>EDIT</button>}
            {publicProfileUrl && <a className="ae-idbar__btn" href={publicProfileUrl} target="_blank" rel="noopener noreferrer">PUBLIC PROFILE ↗</a>}
            <button className="ae-idbar__btn ae-idbar__btn--solid" onClick={onSendMessage}>SEND MESSAGE</button>
          </>
        )}
      </div>
    </div>
  );
}
